<template>
  <div class="cf-color">
    <span class="cf-color__label">{{ label }}</span>
    <div class="cf-color__controls">
      <div class="cf-color__presets" v-if="presets.length">
        <button
          v-for="preset in presets"
          :key="preset"
          type="button"
          class="cf-color__preset"
          :class="{ 'cf-color__preset--active': isActive(preset) }"
          :style="{ background: preset }"
          :title="preset"
          @click="$emit('input', preset)"
        >
          <i v-if="isActive(preset)" class="tim-icons icon-check-2 cf-color__preset-check"></i>
        </button>
      </div>

      <!--
        Hex pill: editable hex input + visual color picker.
        - Swatch chip on the left: click to open the picker
        - Text input in the middle: type any 3/6/8-digit hex (with or without #)
          Commits on blur/Enter; reverts to the previous valid value on
          invalid input or Esc.
        - Chevron button on the right: opens the picker
        The el-color-picker is mounted but its own trigger is hidden; the
        chevron button programmatically clicks it so the popper anchors here.
      -->
      <div class="cf-color__hex">
        <span
          class="cf-color__hex-chip"
          :style="{ background: value }"
          role="button"
          tabindex="-1"
          :title="`Pick custom color (${formattedHex})`"
          @click="openPicker"
        ></span>
        <input
          ref="hexInput"
          v-model="hexDraft"
          type="text"
          class="cf-color__hex-input"
          spellcheck="false"
          autocomplete="off"
          maxlength="9"
          :aria-label="`${label} hex value`"
          @focus="onHexFocus"
          @blur="commitHex"
          @keydown.enter.prevent="commitHex"
          @keydown.esc="cancelHex"
        />
        <button
          type="button"
          class="cf-color__hex-chev-btn"
          :aria-label="`Open color picker for ${label}`"
          @click="openPicker"
        >
          <i class="tim-icons icon-minimal-down cf-color__hex-chev"></i>
        </button>
        <el-color-picker
          ref="picker"
          :value="value"
          @change="onChange"
          :predefine="presets"
          color-format="hex"
          show-alpha
          size="small"
          class="cf-color__picker"
          popper-class="cf-color__picker-popper"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ColorPicker } from 'element-ui';

const DEFAULT_PRESETS = [
  '#ffffff',
  '#000000',
  '#e14eca',
  '#1d8cf8',
  '#00f2c3',
  '#27293d',
];

// Normalize whatever the el-color-picker emits (hex or rgba) into a single
// hex format. Opaque values stay 6-digit; alpha < 1 produces #RRGGBBAA.
// One canonical format means the chip never has to render two different
// notations side by side.
function normalizeToHex(value) {
  if (!value) return '#000000';
  if (/^#[0-9a-fA-F]{3,8}$/.test(value)) return value.toLowerCase();
  const m = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/i);
  if (!m) return value;
  const hex2 = (n) => Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, '0');
  const r = hex2(m[1]);
  const g = hex2(m[2]);
  const b = hex2(m[3]);
  const a = m[4] !== undefined ? Number(m[4]) : 1;
  if (a >= 1) return `#${r}${g}${b}`;
  const alpha = Math.round(a * 255).toString(16).padStart(2, '0');
  return `#${r}${g}${b}${alpha}`;
}

// Parse user-typed hex (with or without #, 3/6/8 digit). Returns a
// normalized lowercase hex string or null if the input doesn't match.
function parseHexInput(str) {
  if (str == null) return null;
  const cleaned = String(str).trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]+$/.test(cleaned)) return null;
  if (cleaned.length === 3) {
    return ('#' + cleaned.split('').map((c) => c + c).join('')).toLowerCase();
  }
  if (cleaned.length === 6 || cleaned.length === 8) {
    return ('#' + cleaned).toLowerCase();
  }
  return null;
}

export default {
  name: 'ColorField',
  components: { [ColorPicker.name]: ColorPicker },
  props: {
    value: { type: String, required: true },
    label: { type: String, required: true },
    presets: { type: Array, default: () => DEFAULT_PRESETS },
  },
  data() {
    return {
      // Local draft for the editable hex input. Synced from `value` via
      // the watcher below, but isolated while the user is mid-edit so the
      // field doesn't jump as they type.
      hexDraft: '',
      editingHex: false,
    };
  },
  watch: {
    formattedHex: {
      immediate: true,
      handler(newValue) {
        if (!this.editingHex) this.hexDraft = newValue;
      },
    },
  },
  computed: {
    // Always show uppercase hex in the chip. 6-digit for opaque, 8-digit
    // (#RRGGBBAA) for semi-transparent. If a legacy rgba() value sneaks
    // through (from a value persisted before this normalization), convert
    // it on the fly so the UI is always one format.
    formattedHex() {
      if (!this.value) return '';
      if (/^#[0-9a-fA-F]{3,8}$/.test(this.value)) {
        return this.value.toUpperCase();
      }
      return normalizeToHex(this.value).toUpperCase();
    },
  },
  methods: {
    onChange(v) {
      if (!v) {
        this.$emit('input', '#000000');
        return;
      }
      this.$emit('input', normalizeToHex(v));
    },
    isActive(preset) {
      if (!this.value) return false;
      return preset.toLowerCase() === this.value.toLowerCase();
    },
    openPicker() {
      // el-color-picker's own trigger swatch is hidden via CSS; dispatch a
      // click on it to open the popper. Keeps the popper anchored to our
      // visible pill via DOM nesting.
      const trigger = this.$refs.picker?.$el?.querySelector('.el-color-picker__trigger');
      if (trigger) trigger.click();
    },
    onHexFocus() {
      this.editingHex = true;
      // Pre-select so typing replaces immediately.
      this.$nextTick(() => {
        const el = this.$refs.hexInput;
        if (el && typeof el.select === 'function') el.select();
      });
    },
    commitHex() {
      const parsed = parseHexInput(this.hexDraft);
      if (parsed) {
        // Only emit if it's actually different — avoids the parent re-rendering
        // on a no-op blur.
        if (parsed.toLowerCase() !== String(this.value || '').toLowerCase()) {
          this.$emit('input', parsed);
        }
      } else {
        // Invalid — silently revert to the last good value.
        this.hexDraft = this.formattedHex;
      }
      this.editingHex = false;
    },
    cancelHex() {
      this.hexDraft = this.formattedHex;
      this.editingHex = false;
      const el = this.$refs.hexInput;
      if (el && typeof el.blur === 'function') el.blur();
    },
  },
};
</script>

<style lang="scss" scoped>
.cf-color {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px 0;

  &__label {
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.95);
    line-height: 1.2;
    white-space: nowrap;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__presets {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__preset {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    cursor: pointer;
    padding: 0;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.12s ease, transform 0.12s ease;

    &:hover {
      border-color: rgba(255, 255, 255, 0.5);
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px rgba(225, 78, 202, 0.6);
    }

    &--active {
      border: 2px solid #e14eca;
    }
  }

  &__preset-check {
    font-size: 10px;
    // Auto-contrast with the swatch via mix-blend-mode. Reads white on dark
    // swatches, dark on light ones — no per-color computation needed.
    color: rgba(255, 255, 255, 0.95);
    mix-blend-mode: difference;
  }

  // Hex pill — small swatch + editable monospace hex input + chevron
  // button. Single custom-color affordance per row. The pill itself
  // doesn't capture clicks — only the chip and chevron do, so clicking
  // into the input goes straight to typing.
  &__hex {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 4px 4px 6px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.04);
    outline: none;
    position: relative;
    transition: border-color 0.12s ease, background 0.12s ease;

    &:hover {
      border-color: rgba(255, 255, 255, 0.32);
      background: rgba(255, 255, 255, 0.06);
    }
    &:focus-within {
      border-color: #e14eca;
      box-shadow: 0 0 0 2px rgba(225, 78, 202, 0.35);
    }
  }

  &__hex-chip {
    width: 16px;
    height: 16px;
    border-radius: 3px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    flex-shrink: 0;
    cursor: pointer;
  }

  &__hex-input {
    background: transparent;
    border: none;
    outline: none;
    padding: 0;
    width: 76px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    letter-spacing: 0.02em;
    line-height: 1;
    text-transform: uppercase;

    &::selection { background: rgba(225, 78, 202, 0.4); }
  }

  &__hex-chev-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.4);
    transition: color 0.12s ease, background 0.12s ease;

    &:hover {
      color: rgba(255, 255, 255, 0.9);
      background: rgba(255, 255, 255, 0.06);
    }
  }

  &__hex-chev {
    font-size: 10px;
  }

  // Hide el-color-picker's default trigger; keep the component mounted so
  // its popper still works. Absolute positioning + zero size removes it
  // from the visual flow while a programmatic click() still opens it.
  &__picker {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
  }
}
</style>

<!--
  Element UI's color-picker popover teleports to body, so scoped styles
  can't reach it. We target it via popper-class. The internal text input
  is hidden because Element UI 2.15 falls back to rgba in that field
  regardless of color-format="hex" — users can still pick visually + use
  the alpha slider + presets, just can't type a literal value here.
  The Clear/OK buttons remain.
-->
<style>
.cf-color__picker-popper .el-color-dropdown__value {
  display: none;
}

.white-content .cf-color__label {
  color: rgba(0, 0, 0, 0.9);
}
.white-content .cf-color__preset {
  border-color: rgba(0, 0, 0, 0.15);
}
.white-content .cf-color__preset:hover {
  border-color: rgba(0, 0, 0, 0.4);
}
.white-content .cf-color__hex {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.12);
}
.white-content .cf-color__hex:hover {
  background: rgba(0, 0, 0, 0.05);
  border-color: rgba(0, 0, 0, 0.3);
}
.white-content .cf-color__hex-chip {
  border-color: rgba(0, 0, 0, 0.18);
}
.white-content .cf-color__hex-input {
  color: rgba(0, 0, 0, 0.9);
}
.white-content .cf-color__hex-chev-btn {
  color: rgba(0, 0, 0, 0.4);
}
.white-content .cf-color__hex-chev-btn:hover {
  color: rgba(0, 0, 0, 0.9);
  background: rgba(0, 0, 0, 0.04);
}
</style>
